import type { WritableComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

import { DisciplinePaymentOption, DisciplineSubscriptionType } from './enums'
import { useDisciplinePaymentLinksStore } from './payment-links/store'
import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplinePaymentCalculate } from './discipline-payment-calculate.hook'
import {
  splitDisciplinePaymentProducts,
  toPaymentDiscountOptions,
  toPaymentProductOptions,
} from './discipline-payment-links'

interface DisciplinePaymentOptionsReturn {
  refIsOneTime: ComputedRef<boolean>
  refIsInstallment: ComputedRef<boolean>
  refCanChooseOneTime: ComputedRef<boolean>
  refCanChooseInstallment: ComputedRef<boolean>
  refIsBusy: ComputedRef<boolean>
  refIsAutoRenew: ComputedRef<boolean>
  refIsFourWeeks: ComputedRef<boolean>
  refPackageOptions: ComputedRef<NativeSelectOption[]>
  refInstallmentOptions: ComputedRef<NativeSelectOption[]>
  refDiscountOptions: ComputedRef<NativeSelectOption[]>
  refExpiresAt: WritableComputedRef<Date | null>
  refCustomerNow: ComputedRef<Date | undefined>
  refTimezone: ComputedRef<string>
  setPaymentOption: (option: DisciplinePaymentOption) => void
  setAutoRenew: (value: boolean) => void
  setPackage: (option: NativeSelectOption) => void
  setInstallment: (option: NativeSelectOption) => void
  setDiscount: (option: NativeSelectOption) => void
}

const ALL_OPTIONS = [DisciplinePaymentOption.OneTime, DisciplinePaymentOption.Installment]

const toId = (option: NativeSelectOption): number | null => {
  const id = Number(option.value)
  return Number.isInteger(id) && id > 0 ? id : null
}

/** Every change that alters what a generated link was made for throws that link away: it would send a stale order. */
export const useDisciplinePaymentOptions = (): DisciplinePaymentOptionsReturn => {
  const dayjs = useDayjs()
  const store = useDisciplinePaymentLinksStore()
  const disciplineStore = useDisciplineStore()
  const profileStore = useProfileStore()
  const { canManageOrders, canManageSubscriptions } = useDisciplinePrivileges()
  const { recalculate } = useDisciplinePaymentCalculate()

  const refProducts = computed(() =>
    splitDisciplinePaymentProducts(store.getProducts, disciplineStore.getDetails?.code ?? null))

  const refIsOneTime = computed<boolean>(() => store.isOneTime)
  const refIsInstallment = computed<boolean>(() => store.isInstallment)
  /** One-time packages exist only for some pricing models; without one the option cannot be priced. */
  const refCanChooseOneTime = computed<boolean>(() => canManageOrders.value && refProducts.value.oneTime.length > 0)
  const refCanChooseInstallment = computed<boolean>(() => canManageSubscriptions.value)
  const refIsBusy = computed<boolean>(() => store.isCalculating || store.isGenerating)
  const refIsAutoRenew = computed<boolean>(() => store.isAutoRenew)
  const refIsFourWeeks = computed<boolean>(() =>
    store.getSubscriptionCalculation?.type === DisciplineSubscriptionType.FourWeeks)

  const refPackageOptions = computed<NativeSelectOption[]>(() =>
    toPaymentProductOptions(refProducts.value.oneTime, store.getPackageId))
  const refInstallmentOptions = computed<NativeSelectOption[]>(() =>
    toPaymentProductOptions(refProducts.value.subscriptions, store.getInstallmentId))
  const refDiscountOptions = computed<NativeSelectOption[]>(() =>
    toPaymentDiscountOptions(profileStore.options?.discounts ?? [], store.getDiscount))

  const refTimezone = computed<string>(() => disciplineStore.getDetails?.customer?.timezone ?? '')

  /** The picker works in the browser's clock, so the customer's wall-clock "now" is rebuilt as a local date. */
  const refCustomerNow = computed<Date | undefined>(() => {
    if (!refTimezone.value) return undefined

    try {
      return dayjs(dayjs().tz(refTimezone.value).format(DateFormat.Default)).toDate()
    } catch {
      return undefined
    }
  })

  /** Sent as a naive 'Y-m-d H:i:s' wall clock that the server reads in the customer's timezone. */
  const refExpiresAt = computed<Date | null>({
    get: () => store.getExpiresAt ? dayjs(store.getExpiresAt).toDate() : null,
    set: (value) => {
      store.setExpiresAt(value ? dayjs(value).format(DateFormat.Default) : null)
      store.dropLinks(ALL_OPTIONS)
    },
  })

  const setPaymentOption = (option: DisciplinePaymentOption): void => {
    if (store.getPaymentOption === option) return

    store.setPaymentOption(option)
    void recalculate()
  }

  const setAutoRenew = (value: boolean): void => {
    store.setAutoRenew(value)
    store.dropLinks([DisciplinePaymentOption.Installment])
  }

  const setPackage = (option: NativeSelectOption): void => {
    store.setPackageId(toId(option))
    store.setOrderCalculation(null)
    store.dropLinks([DisciplinePaymentOption.OneTime])
    void recalculate()
  }

  const setInstallment = (option: NativeSelectOption): void => {
    store.setInstallmentId(toId(option))
    store.setSubscriptionCalculation(null)
    store.dropLinks([DisciplinePaymentOption.Installment])
    void recalculate()
  }

  const setDiscount = (option: NativeSelectOption): void => {
    store.setDiscount(Number(option.value) || 0)
    store.dropLinks(ALL_OPTIONS)
    void recalculate()
  }

  return {
    refIsOneTime,
    refIsInstallment,
    refCanChooseOneTime,
    refCanChooseInstallment,
    refIsBusy,
    refIsAutoRenew,
    refIsFourWeeks,
    refPackageOptions,
    refInstallmentOptions,
    refDiscountOptions,
    refExpiresAt,
    refCustomerNow,
    refTimezone,
    setPaymentOption,
    setAutoRenew,
    setPackage,
    setInstallment,
    setDiscount,
  }
}
